import {
  useCallback,
  useState,
  useEffect,
  useRef,
  MutableRefObject,
  MouseEvent,
} from 'react';
import { Box, Flex, Grid } from 'components/box';
import {
  ProductGalleryItemInterface,
  ProductGalleryItemTypeEnum,
  SoldOutHeaderInterface,
} from 'types/types';
import {
  fadeAnimations,
  soldOutWrapperStyle,
} from '../../../../../shared/style';
import { isGalleryImage, isGalleryVideo } from 'types/guards/product';
import SoldOutMask from 'components/sold-out-mask';
import Thumbnail from './thumbnail';
import ProductLabel from './product-label';
import resourceUrl from '../../../../../util/make-url';
import ThumbnailNavigationButton, {
  NavigationButtonType,
} from './thumbnail-navigation-button';
import { css } from '@emotion/react';
import preventDefaultAction from '../../../../../util/prevent-default-action';
import dynamic from 'next/dynamic';
import { zIndices } from 'constants/z-indices';
import { useResized } from 'hooks/display';

const EmbeddedVideo = dynamic(
  () => import('components/_shared/widgets/embedded-video')
);

const THUMBNAIL_SCROLL_OFFSET = 17;
const ZOOM_SCALE = 1.5;

let timeoutId;

const setZoomOnRefElement = (
  refElement: MutableRefObject<HTMLImageElement | null>,
  isZoomActive: boolean,
  e: MouseEvent<HTMLElement>
) => {
  if (refElement?.current === null || !isZoomActive) {
    return;
  }
  const top = refElement.current.getBoundingClientRect().top || 0;
  const left = refElement.current.getBoundingClientRect().left || 0;

  const width = refElement.current.clientWidth || 0;
  const height = refElement.current.clientHeight || 0;
  const x_ = e.clientX - left;
  const y_ = e.clientY - top;

  const x = (1 - x_ / width) * ZOOM_SCALE * 12.5;
  const y = (1 - y_ / height) * ZOOM_SCALE * 12.5;
  refElement.current.style.transform = `scale(${ZOOM_SCALE}) translate(${x}%, ${y}%)`;
};

const ActiveMedia = ({
  activeMedia,
  soldOut,
  hideThumbnail = false,
  offsetActiveIndex,
  prevEnabled,
  endOfCarousel,
  dealTypes,
  soldOutHeaders,
}: {
  activeMedia: ProductGalleryItemInterface;
  soldOut: boolean;
  hideThumbnail?: boolean;
  offsetActiveIndex: (offset: number) => void;
  prevEnabled: boolean;
  endOfCarousel: boolean;
  soldOutHeaders?: SoldOutHeaderInterface[];
  dealTypes?: string[];
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const [isZoomActive, setIsZoomActive] = useState(false);
  const [canShowSoldOutOverlay, setCanShowSoldOutOverlay] = useState(true);

  const src = resourceUrl(activeMedia.file.url, {
    width: 800,
    height: 800,
    bg: 'fff',
    fit: 'fill',
  });

  const alt = activeMedia.label || 'Product media';

  const onMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      setZoomOnRefElement(imgRef, isZoomActive, e);
    },
    [isZoomActive]
  );

  const disableZoom = useCallback(() => {
    if (imgRef.current === null) {
      return;
    }
    imgRef.current.style.transform = 'unset';
    setIsZoomActive(false);
  }, []);

  useEffect(() => {
    const galleryElement = galleryRef.current;
    if (galleryElement) {
      timeoutId && clearTimeout(timeoutId);

      galleryElement.classList.remove('galleryTransitionIn');
      galleryElement.classList.add('galleryTransitionOut');

      timeoutId = setTimeout(() => {
        galleryElement.classList.remove('galleryTransitionOut');
        galleryElement.classList.add('galleryTransitionIn');
      }, 30);
    }
  }, [activeMedia]);

  return (
    <div
      role="button"
      tabIndex={-1}
      onContextMenu={preventDefaultAction}
      onMouseMove={onMouseMove}
      onMouseLeave={disableZoom}
      css={theme => css`
        grid-area: main;
        position: relative;
        width: 100%;
        border-radius: 20px;
        box-shadow: ${theme.shadows.cards};

        &:after {
          content: '';
          display: block;
          padding-bottom: 100%;
        }

        & .thumbnail-navigation {
          position: absolute;
          top: calc(50% - 16px);
        }
        ${hideThumbnail
          ? css`
              grid-column: 1 / span 2;
            `
          : null}
      `}
    >
      <Box
        borderRadius="20px"
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        onMouseEnter={() => setCanShowSoldOutOverlay(false)}
        onMouseLeave={() => setCanShowSoldOutOverlay(true)}
        css={css`
          overflow: hidden;

          ${fadeAnimations}

          .soldOutWrapper {
            ${soldOutWrapperStyle}
            animation-duration: 500ms;
            animation-fill-mode: both;
            ${canShowSoldOutOverlay ? 'animation-name: fadeIn' : null};
          }

          &:hover {
            .soldOutWrapper {
              animation-name: fadeOut;
            }
          }
        `}
      >
        {soldOut && canShowSoldOutOverlay && (
          <div className="soldOutWrapper">
            <SoldOutMask
              clock
              text
              soldOutHeaders={soldOutHeaders}
              dealTypes={dealTypes}
            />
          </div>
        )}

        {isGalleryVideo(activeMedia) && (
          <div
            css={css`
              position: absolute;
              top: 50%;
              left: 0;
              width: 100%;
              height: 100%;
              transform: translateY(-50%);
            `}
          >
            <EmbeddedVideo
              title={activeMedia.label}
              url={activeMedia.video.url}
              provider={activeMedia.video.provider}
              isVideoActive
            />
          </div>
        )}

        {isGalleryImage(activeMedia) && (
          <Box
            onClick={(e: MouseEvent<HTMLElement>) => {
              if (isZoomActive) {
                disableZoom();
              } else {
                setIsZoomActive(true);
                setZoomOnRefElement(imgRef, true, e);
              }
            }}
            onMouseLeave={disableZoom}
            ref={galleryRef}
            id="galleryImageMain"
            className="galleryTransitionIn"
          >
            <img
              ref={imgRef}
              src={src}
              alt={alt}
              draggable={false}
              css={css`
                display: block;
                height: 100%;
                width: 100%;
                position: absolute;
                object-fit: contain;

                &:hover {
                  cursor: ${isZoomActive ? 'zoom-out' : 'zoom-in'};
                }
              `}
            />
          </Box>
        )}

        {activeMedia.label && <ProductLabel text={activeMedia.label} />}
      </Box>

      {!hideThumbnail && (
        <div
          className="thumbnail-navigation"
          css={css`
            left: 10px;
            opacity: 1;
            z-index: ${zIndices.CAROUSEL_NAVIGATION};
            transition: all 0.3s ease-in-out;

            ${!prevEnabled &&
            css`
              opacity: 0;
              z-index: ${zIndices.TEN_BELOW};
            `}
          `}
        >
          <ThumbnailNavigationButton
            disableZoom={() => setIsZoomActive(false)}
            setCanShowSoldOutOverlay={setCanShowSoldOutOverlay}
            type={NavigationButtonType.Previous}
            onClick={() => offsetActiveIndex(-1)}
            isEnabled={prevEnabled}
          />
        </div>
      )}

      {!hideThumbnail && (
        <div
          className="thumbnail-navigation"
          css={css`
            right: 10px;
            opacity: 1;
            z-index: ${zIndices.CAROUSEL_NAVIGATION};
            transition: opacity 0.3s ease-in-out;
          `}
        >
          <ThumbnailNavigationButton
            disableZoom={() => setIsZoomActive(false)}
            setCanShowSoldOutOverlay={setCanShowSoldOutOverlay}
            type={
              !endOfCarousel
                ? NavigationButtonType.Next
                : NavigationButtonType.ReturnToStart
            }
            onClick={() => offsetActiveIndex(+1)}
            isEnabled
          />
        </div>
      )}
    </div>
  );
};

const getThumbWidths = (thumbs: HTMLDivElement | null): number[] =>
  thumbs
    ? Array.from(thumbs.children)
        .filter(image => image instanceof HTMLElement)
        .map(image => image.getBoundingClientRect().width)
    : [];

const ThumbnailWrapper = ({
  media,
  activeMediaIndex,
  setActiveMediaIndex,
  prevEnabled,
  offsetActiveIndex,
  endOfCarousel,
}: {
  media: ProductGalleryItemInterface[];
  activeMediaIndex: number;
  setActiveMediaIndex: (idx: number) => void;
  prevEnabled: boolean;
  offsetActiveIndex: (offset: number) => number;
  endOfCarousel: boolean;
}) => {
  const thumbWidthsRef = useRef<number[]>([]);

  const thumbs = useRef<HTMLDivElement | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);

  const canScroll = useRef(true);
  const isScrolling = useRef(false);

  const scroll = useCallback((index: number) => {
    const thumbWidths = thumbWidthsRef.current;

    if (
      !scroller.current ||
      isScrolling.current ||
      thumbWidths.length === 0 ||
      typeof window === 'undefined'
    ) {
      return;
    }

    const totalWidths = thumbWidths.reduce((total, width) => total + width, 0);

    isScrolling.current = true;

    window.requestAnimationFrame(() => {
      if (!scroller.current) return;

      const scrollerWidth = scroller.current.clientWidth;
      const thumbsToSkip =
        Math.floor((scrollerWidth * thumbWidths.length) / (2 * totalWidths)) -
        1;

      const sliceDistance = Math.max(index - thumbsToSkip, 0);
      const scrollOffset = thumbWidths
        .slice(0, sliceDistance)
        .reduce((offset, width) => offset + width + THUMBNAIL_SCROLL_OFFSET, 0);

      scroller.current.scrollTo({
        left: scrollOffset,
        behavior: 'smooth',
      });

      setTimeout(() => (isScrolling.current = false), 300);
    });
  }, []);

  const navButtonClick = useCallback(
    (type: 'next' | 'prev') => {
      if (isScrolling.current) return;

      const nextIndex = offsetActiveIndex(type === 'next' ? 1 : -1);
      scroll(nextIndex);
    },
    [offsetActiveIndex, scroll]
  );

  const resize = useCallback(() => {
    const nextWidths = getThumbWidths(thumbs.current);
    if (JSON.stringify(nextWidths) !== JSON.stringify(thumbWidthsRef.current)) {
      thumbWidthsRef.current = nextWidths;
    }
  }, []);

  useResized({
    ref: thumbs,
    callback: resize,
  });

  useEffect(() => {
    if (canScroll.current && !isScrolling.current) {
      scroll(activeMediaIndex);
    }
  }, [activeMediaIndex, scroll]);

  return (
    <Flex
      overflow="hidden"
      position="relative"
      className="hide-for-mobile-only"
      onMouseEnter={() => (canScroll.current = false)}
      onMouseLeave={() => (canScroll.current = true)}
      css={css`
        &:hover .thumbnail-navigation {
          opacity: 1;
          pointer-events: auto;
        }
      `}
    >
      <div
        className="thumbnail-navigation"
        css={css`
          left: 3px;
          top: calc(50% - 16px);
          opacity: 1;
          z-index: ${zIndices.CAROUSEL_NAVIGATION};
          transition: opacity 0.3s ease-in-out;
          position: absolute;
          opacity: 0;
          pointer-events: none;
        `}
      >
        <ThumbnailNavigationButton
          type={NavigationButtonType.Previous}
          onClick={() => navButtonClick('prev')}
          isEnabled={prevEnabled}
        />
      </div>
      <div
        ref={scroller}
        css={theme => css`
          grid-area: thumbs;
          position: relative;
          align-self: center;
          overflow: hidden;
          @media ${theme.mediaQueries.desktopUp} {
            max-height: 360px;
          }
          @media ${theme.mediaQueries.tabletDown} {
            max-height: 660px;
            .soldOutWrapper {
              display: none;
            }
          }

          ${fadeAnimations}

          .soldOutWrapper {
            ${soldOutWrapperStyle}
            animation-duration: 500ms;
            animation-fill-mode: both;
            animation-name: fadeIn;
          }

          &:hover {
            .soldOutWrapper {
              animation-name: fadeOut;
            }
          }
        `}
      >
        <Grid
          ref={thumbs}
          gridTemplateColumns={`repeat(${media.length + 1}, 95px)`}
          gridColumnGap="10px"
          mt={0}
          py="10px"
        >
          {media.map((media, idx) => (
            <Thumbnail
              item={media}
              key={idx}
              isActive={activeMediaIndex === idx}
              setActive={() => setActiveMediaIndex(idx)}
              isVideo={[
                ProductGalleryItemTypeEnum.ExternalVideo,
                ProductGalleryItemTypeEnum.Video,
              ].includes(media.type)}
            />
          ))}
          <div
            css={css`
              width: 5px;
            `}
          />
        </Grid>
      </div>
      <div
        className="thumbnail-navigation"
        css={css`
          right: 3px;
          top: calc(50% - 16px);
          opacity: 1;
          z-index: ${zIndices.CAROUSEL_NAVIGATION};
          transition: opacity 0.3s ease-in-out;
          position: absolute;
          opacity: 0;
          pointer-events: none;
        `}
      >
        <ThumbnailNavigationButton
          type={
            !endOfCarousel
              ? NavigationButtonType.Next
              : NavigationButtonType.ReturnToStart
          }
          onClick={() => navButtonClick('next')}
          isEnabled
        />
      </div>
    </Flex>
  );
};

const DesktopCarousel = ({
  media,
  isSoldOut,
  activeMediaIndex,
  hideThumbnail = false,
  setActiveMediaIndex,
  soldOutHeaders,
  dealTypes,
}: {
  media: ProductGalleryItemInterface[];
  isSoldOut: boolean;
  activeMediaIndex: number;
  hideThumbnail?: boolean;
  setActiveMediaIndex: (idx: number) => void;
  soldOutHeaders?: SoldOutHeaderInterface[];
  dealTypes?: string[];
}) => {
  const maxPossibleIndex = media.length - 1;
  const prevEnabled = activeMediaIndex > 0;
  const endOfCarousel = activeMediaIndex === media.length - 1;
  const hasMultipleMediaEntries = media.length >= 2;

  const offsetActiveIndex = useCallback(
    (offset: number) => {
      const newIndex =
        activeMediaIndex + offset >= media.length
          ? 0
          : activeMediaIndex + offset < 0
          ? activeMediaIndex
          : activeMediaIndex + offset;

      setActiveMediaIndex(newIndex);
      return newIndex;
    },
    [activeMediaIndex, media.length, setActiveMediaIndex]
  );

  const activeThumbIndex = Math.min(activeMediaIndex, maxPossibleIndex);
  const activeMedia = media[activeThumbIndex] || {};

  return (
    <>
      <ActiveMedia
        activeMedia={activeMedia}
        soldOut={isSoldOut}
        hideThumbnail={!hasMultipleMediaEntries || hideThumbnail}
        prevEnabled={prevEnabled}
        endOfCarousel={endOfCarousel}
        offsetActiveIndex={offsetActiveIndex}
        soldOutHeaders={soldOutHeaders}
        dealTypes={dealTypes}
      />
      {!hideThumbnail && hasMultipleMediaEntries && (
        <ThumbnailWrapper
          media={media}
          activeMediaIndex={activeThumbIndex}
          setActiveMediaIndex={setActiveMediaIndex}
          prevEnabled={prevEnabled}
          endOfCarousel={endOfCarousel}
          offsetActiveIndex={offsetActiveIndex}
        />
      )}
    </>
  );
};

export default DesktopCarousel;
